import { LayoutUtils } from '@/utils/LayoutUtils'

// ---------------------------------------------------------------------------
// Scene-thumbnail sizing constants
// These govern the height range for scene thumbnail cards in the side panel.
// Width is fixed; height is clamped based on the scene's aspect ratio.
// ---------------------------------------------------------------------------

export const SCENE_THUMBNAIL_BASE_WIDTH = 64
export const SCENE_THUMBNAIL_MIN_HEIGHT = 42
export const SCENE_THUMBNAIL_MAX_HEIGHT = 100

// ---------------------------------------------------------------------------
// Sizing utility
// ---------------------------------------------------------------------------

/**
 * The same sizing turned sideways, for a horizontal row of thumbnails: the
 * height is fixed and the width follows the scene's ratio, clamped to the
 * range the side panel allows for heights.
 */
export function getSceneThumbnailSizesByHeight(
  dimensions: { width?: number | null; height?: number | null } | null | undefined,
  height: number,
  { minWidth = SCENE_THUMBNAIL_MIN_HEIGHT, maxWidth = SCENE_THUMBNAIL_MAX_HEIGHT }: { minWidth?: number; maxWidth?: number } = {},
): { width: number; height: number } {
  const width = dimensions?.width && dimensions.width > 0 ? dimensions.width : 1080
  const sourceHeight = dimensions?.height && dimensions.height > 0 ? dimensions.height : 1080
  return { width: LayoutUtils.clamp(height * (width / sourceHeight), minWidth, maxWidth), height }
}

export function getSceneThumbnailSizes(
  dimensions?: { width?: number | null; height?: number | null } | null,
  options: {
    baseWidth?: number
    activeWidth?: number
    minHeight?: number
    maxHeight?: number
  } = {},
): {
  baseSize: { width: number; height: number }
  activeSize: { width: number; height: number }
  ratio: number
} {
  const baseWidth = options.baseWidth ?? SCENE_THUMBNAIL_BASE_WIDTH
  const activeWidth = options.activeWidth ?? baseWidth
  const minHeight = options.minHeight ?? SCENE_THUMBNAIL_MIN_HEIGHT
  const maxHeight = options.maxHeight ?? SCENE_THUMBNAIL_MAX_HEIGHT
  const width = dimensions?.width && dimensions.width > 0 ? dimensions.width : 1080
  const height = dimensions?.height && dimensions.height > 0 ? dimensions.height : 1080
  const ratio = width / height
  const scaleFactor = activeWidth / baseWidth

  return {
    baseSize: {
      width: baseWidth,
      height: LayoutUtils.clamp(baseWidth / ratio, minHeight, maxHeight),
    },
    activeSize: {
      width: activeWidth,
      height: LayoutUtils.clamp(activeWidth / ratio, minHeight * scaleFactor, maxHeight * scaleFactor),
    },
    ratio,
  }
}
